/*
 * The Belgian Brewer: brass poster frames for sections/tbb-hero.liquid (with vine) and sections/tbb-book.liquid (frame only).
 * Draws a tapering brass bar with asymmetric whiplash sweeps from the poster's measured size,
 * then a green vine that inks itself around the inside of that contour while leaves unfurl.
 * Without this script the poster keeps its plain CSS brass border, so the page is complete.
 * Motion is CSS-only and switched off under prefers-reduced-motion (see tbb-hero.css).
 */
const NS = 'http://www.w3.org/2000/svg';
const r1 = (v) => Math.round(v * 10) / 10;

const make = (tag, attrs, parent) => {
  const el = document.createElementNS(NS, tag);
  for (const key in attrs) el.setAttribute(key, attrs[key]);
  if (parent) parent.appendChild(el);
  return el;
};

function geometry(W, H) {
  const cH = Math.min(230, Math.max(84, W * 0.17));
  const bH = Math.min(210, Math.max(70, W * 0.15));
  return { W, H, cH, cV: Math.min(180, cH * 0.8), bH, bV: Math.min(220, bH * 1.06), s: 18, bow: W < 700 ? 3 : 7, dip: W < 700 ? 6 : 20 };
}

/* closed contour at per-side insets l,t,r,b (thick left, thin right: the bar tapers as it turns) */
function contour(g, l, t, r, b) {
  const { W, H, cH, cV, bH, bV, s, bow, dip } = g;
  const x0 = l + cH;
  const x3 = W - r - s;
  const sp = x3 - x0;
  const y1 = H - b - s;
  const y2 = t + cV;
  const len = y1 - y2;
  return (
    `M${l} ${r1(y1)}C${r1(l + bow)} ${r1(y1 - len * 0.33)} ${r1(l + bow)} ${r1(y2 + len * 0.33)} ${l} ${r1(y2)}` +
    `C${l} ${r1(t + cV * 0.45)} ${r1(l + cH * 0.42)} ${t} ${r1(x0)} ${t}` +
    `C${r1(x0 + sp * 0.3)} ${r1(t + dip)} ${r1(x0 + sp * 0.62)} ${t} ${r1(x3)} ${t}` +
    `C${r1(x3 + s * 0.55)} ${t} ${W - r} ${r1(t + s * 0.45)} ${W - r} ${r1(t + s)}` +
    `L${W - r} ${r1(H - b - bV)}C${W - r} ${r1(H - b - bV * 0.45)} ${r1(W - r - bH * 0.42)} ${H - b} ${r1(W - r - bH)} ${H - b}` +
    `L${r1(l + s)} ${H - b}C${r1(l + s * 0.45)} ${H - b} ${l} ${r1(H - b - s * 0.45)} ${l} ${r1(y1)}Z`
  );
}

function drawFrame(svg, g, band, gutter) {
  svg.setAttribute('viewBox', `0 0 ${g.W} ${g.H}`);
  svg.textContent = '';
  const outer = contour(g, 3, 3, 3, 3);
  const inner = contour(g, 11, 7.5, 5.5, 9);
  const hair = contour(g, 17, 13, 11, 15);
  const W = g.W;
  make('path', { d: outer, class: 'paper' }, svg);

  if (band) {
    const [bandTop, bandH] = band;
    const clipId = `${svg.id}-clip`;
    const clip = make('clipPath', { id: clipId }, svg);
    make('path', { d: inner }, clip);
    const group = make('g', { 'clip-path': `url(#${clipId})` }, svg);
    make('rect', { x: 0, y: bandTop, width: W, height: bandH, class: 'band' }, group);
    make('path', { d: `M0 ${bandTop + 0.5}H${W}M0 ${bandTop + 4.5}H${W}`, class: 'rule' }, group);
  }

  make('path', { d: outer + inner, class: 'bar' }, svg);
  make('path', { d: hair, class: 'hair' }, svg);

  /* whiplash tendrils spring out of the bar into the margins, tapering from frame to spiral */
  if (gutter >= 40) {
    const y = r1(g.cV + 64);
    const scale = Math.min(1, (gutter - 6) / 62).toFixed(2);
    const d = `M3 ${y}c-20-24-14-70-32-90-12-16-32-6-27 12 4 12 18 9 16-1`;
    ['', `rotate(180 ${W / 2} ${g.H / 2}) `].forEach((rot) => {
      const group = make('g', { class: 'whip', transform: `${rot}translate(3 ${y}) scale(${scale}) translate(-3 -${y})` }, svg);
      [[7, 0.26], [5, 0.5], [3.2, 0.74], [1.7, 1]].forEach(([w, f]) =>
        make('path', { d, pathLength: 1, style: `stroke-width:${w};stroke-dasharray:${f} 3` }, group)
      );
    });
  }
}

function buildVine(vine, g, bandTop, startTime) {
  const { W, H, cH, cV, dip } = g;
  const narrow = W < 700;
  const m = narrow ? 17 : 27;
  const a = narrow ? 4 : 7;
  const branches = [];
  const s2 = 26;
  const y0 = bandTop - 10;
  const x0 = m + 40;

  let d = `M${x0} ${y0}C${m + 14} ${y0 - 2} ${m} ${y0 - 18} ${m} ${y0 - 46}`;
  let yA = y0 - 46;
  let yB = m + cV;
  let n = Math.max(1, Math.round((yA - yB) / 200));
  let h = (yA - yB) / n;
  for (let i = 0; i < n; i++) {
    const s = i % 2 ? -1 : 1;
    const y1 = yA - h * i;
    const y2 = y1 - h;
    d += `C${r1(m + a * s)} ${r1(y1 - h * 0.33)} ${r1(m + a * s)} ${r1(y2 + h * 0.33)} ${m} ${r1(y2)}`;
    if (!narrow && i === 0 && n > 1) branches.push([d, `M${m} ${r1(y2)}c12-6 26-2 26 10 0 9-11 11-13 4-1-5 5-7 7-3`]);
  }
  d += `C${m} ${r1(m + cV * 0.45)} ${r1(m + cH * 0.42)} ${m} ${r1(m + cH)} ${m}`;

  const xA = m + cH;
  const xB = W - m - s2;
  const sp = xB - xA;
  const sag = (x) => 0.44 * dip * Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, (x - xA) / (sp * 0.66)))), 1.3);
  n = Math.max(1, Math.round(sp / 220));
  const w = sp / n;
  for (let i = 0; i < n; i++) {
    const s = i % 2 ? 1 : -1;
    const x1 = xA + w * i;
    const x2 = x1 + w;
    const c1 = x1 + w * 0.33;
    const c2 = x2 - w * 0.33;
    d += `C${r1(c1)} ${r1(m + sag(c1) + a * s)} ${r1(c2)} ${r1(m + sag(c2) + a * s)} ${r1(x2)} ${r1(m + sag(x2))}`;
    if (!narrow && i === 1 && n > 3) branches.push([d, `M${r1(x2)} ${r1(m + sag(x2))}c4 10-1 22-11 22-7 0-8-8-3-9 3 0 5 3 3 5`]);
  }
  d += `C${r1(xB + s2 * 0.55)} ${m} ${W - m} ${r1(m + s2 * 0.45)} ${W - m} ${r1(m + s2)}`;

  const yC = m + s2;
  const yE = Math.max(yC + 60, bandTop * 0.58);
  n = Math.max(1, Math.round((yE - yC) / 200));
  h = (yE - yC) / n;
  for (let i = 0; i < n; i++) {
    const s = i % 2 ? 1 : -1;
    const y1 = yC + h * i;
    const y2 = y1 + h;
    d += `C${r1(W - m + a * s * 0.8)} ${r1(y1 + h * 0.33)} ${r1(W - m + a * s * 0.8)} ${r1(y2 - h * 0.33)} ${W - m} ${r1(y2)}`;
  }
  d += 'c0 22-12 36-30 36-15 0-20-15-9-20 7-3 12 3 8 8';

  const ghost = vine.querySelector('.gh-g');
  const ink = vine.querySelector('.ink-g');
  const leaves = vine.querySelector('.leaf-g');
  ghost.textContent = '';
  ink.textContent = '';
  leaves.textContent = '';
  vine.setAttribute('viewBox', `0 0 ${W} ${H}`);

  make('path', { d, class: 'gh' }, ghost);
  const main = make('path', { d, class: 'ink', pathLength: 1 }, ink);
  const L = main.getTotalLength();
  const elapsed = (performance.now() - startTime) / 1000;
  const S = 0.15;
  const D = narrow ? 1.4 : 1.8;
  main.style.setProperty('--del', `${S - elapsed}s`);
  main.style.setProperty('--dur', `${D}s`);

  const measure = make('path', {}, ghost);
  branches.forEach(([pre, bd]) => {
    measure.setAttribute('d', pre);
    const f = measure.getTotalLength() / L;
    make('path', { d: bd, class: 'gh' }, ghost);
    const p = make('path', { d: bd, class: 'ink', pathLength: 1 }, ink);
    p.style.setProperty('--del', `${S + D * f - elapsed}s`);
    p.style.setProperty('--dur', '.55s');
  });
  measure.remove();

  let side = 1;
  let j = 0;
  for (let s = 70; s < L - 40; s += narrow ? 84 : 104) {
    const p = main.getPointAtLength(s);
    const q = main.getPointAtLength(s + 2);
    const angle = (Math.atan2(q.y - p.y, q.x - p.x) * 180) / Math.PI;
    const sc = (narrow ? 0.56 : 0.72) + ((j * 37) % 9) / 30;
    const group = make(
      'g',
      { transform: `translate(${r1(p.x)} ${r1(p.y)}) rotate(${r1(angle + side * 52)}) scale(${sc.toFixed(2)} ${(side * sc).toFixed(2)})` },
      leaves
    );
    const leaf = make('use', { href: '#tbb-v-leaf', class: 'vl' }, group);
    leaf.style.setProperty('--del', `${S + (D * s) / L - elapsed - 0.05}s`);
    side = -side;
    j++;
  }
}

function setUp(poster) {
  if (poster.dataset.tbbReady) return;
  poster.dataset.tbbReady = 'true';
  const frame = poster.querySelector('[data-tbb-frame]');
  const vine = poster.querySelector('[data-tbb-vine]');
  const plaques = poster.querySelector('[data-tbb-plaques]');
  const startTime = performance.now();
  let lastW = 0;
  let lastH = 0;

  const build = () => {
    const W = poster.clientWidth;
    const H = poster.clientHeight;
    if (!W || !H || (W === lastW && H === lastH)) return;
    lastW = W;
    lastH = H;
    const g = geometry(W, H);
    const bandTop = plaques ? plaques.offsetTop : H;
    const bandHeight = plaques ? plaques.offsetHeight : 0;
    poster.classList.add('is-drawn');
    drawFrame(frame, g, plaques ? [bandTop, bandHeight] : null, poster.getBoundingClientRect().left);
    if (vine) buildVine(vine, g, bandTop, startTime);
  };

  build();
  if ('ResizeObserver' in window) new ResizeObserver(build).observe(poster);
  else window.addEventListener('resize', build);
}

const init = (root = document) => root.querySelectorAll('[data-tbb-poster]').forEach(setUp);
init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
